"use client"

import { motion, useMotionValue, useSpring, useTransform } from "framer-motion"
import { useRef, useState, useEffect } from "react"
import { Home, User, Briefcase, Mail, Moon, Sun } from "lucide-react"
import { useTheme } from "next-themes"

export function UltraNav() {
    const { theme, setTheme } = useTheme()
    const [mounted, setMounted] = useState(false)

    useEffect(() => {
        setMounted(true)
    }, [])

    const toggleTheme = (e: React.MouseEvent<HTMLButtonElement>) => {
        const x = e.clientX
        const y = e.clientY
        const endRadius = Math.hypot(Math.max(x, window.innerWidth - x), Math.max(y, window.innerHeight - y))

        // @ts-ignore
        if (!document.startViewTransition) {
            setTheme(theme === "dark" ? "light" : "dark")
            return
        }

        // @ts-ignore
        const transition = document.startViewTransition(() => {
            setTheme(theme === "dark" ? "light" : "dark")
        })

        transition.ready.then(() => {
            const clipPath = [`circle(0px at ${x}px ${y}px)`, `circle(${endRadius}px at ${x}px ${y}px)`]
            document.documentElement.animate(
                {
                    clipPath: theme === "dark" ? [...clipPath].reverse() : clipPath,
                },
                {
                    duration: 500,
                    easing: "ease-in",
                    pseudoElement: theme === "dark" ? "::view-transition-old(root)" : "::view-transition-new(root)",
                },
            )
        })
    }

    const links = [
        { icon: Home, label: "Home", href: "#home" },
        { icon: User, label: "About", href: "#about" },
        { icon: Briefcase, label: "Projects", href: "#projects" },
        { icon: Mail, label: "Contact", href: "#contact" },
    ]

    return (
        <nav aria-label="Main navigation" className="fixed bottom-8 left-1/2 -translate-x-1/2 z-50">
            <motion.div
                initial={{ y: 100, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ type: "spring", stiffness: 260, damping: 20 }}
                role="menubar"
                className="flex items-end gap-2 px-4 py-3 rounded-2xl glass-panel"
            >
                {links.map((link) => (
                    <DockItem key={link.label} href={link.href} label={link.label}>
                        <link.icon className="w-5 h-5" aria-hidden="true" />
                        <span className="sr-only">{link.label}</span>
                    </DockItem>
                ))}

                <div className="w-px h-8 bg-border mx-2" role="separator" aria-orientation="vertical" />

                <DockItem
                    onClick={toggleTheme}
                    label={mounted ? (theme === "dark" ? "Switch to light mode" : "Switch to dark mode") : "Toggle theme"}
                >
                    {mounted ? (
                        theme === "dark" ? (
                            <Sun className="w-5 h-5" aria-hidden="true" />
                        ) : (
                            <Moon className="w-5 h-5" aria-hidden="true" />
                        )
                    ) : (
                        <div className="w-5 h-5" />
                    )}
                    <span className="sr-only">
                        {mounted ? (theme === "dark" ? "Switch to light mode" : "Switch to dark mode") : "Toggle theme"}
                    </span>
                </DockItem>
            </motion.div>
        </nav>
    )
}

function DockItem({
    children,
    href,
    onClick,
    label
}: {
    children: React.ReactNode
    href?: string
    onClick?: (e: any) => void
    label?: string
}) {
    const ref = useRef<HTMLDivElement>(null)
    const mouseX = useMotionValue(Infinity)

    const distance = useTransform(mouseX, (val) => {
        const bounds = ref.current?.getBoundingClientRect() ?? { x: 0, width: 0 }
        return val - bounds.x - bounds.width / 2
    })

    const widthSync = useTransform(distance, [-150, 0, 150], [40, 80, 40])
    const width = useSpring(widthSync, { mass: 0.1, stiffness: 150, damping: 12 })

    const content = href ? (
        <a
            href={href}
            className="flex items-center justify-center w-full h-full focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 rounded-xl"
            aria-label={label}
        >
            {children}
        </a>
    ) : (
        <button
            onClick={onClick}
            className="flex items-center justify-center w-full h-full focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 rounded-xl"
            aria-label={label}
            type="button"
        >
            {children}
        </button>
    )

    return (
        <motion.div
            ref={ref}
            style={{ width }}
            role="menuitem"
            className="aspect-square rounded-xl bg-background/50 border border-border flex items-center justify-center relative group cursor-pointer hover:bg-primary/20 transition-colors"
            onMouseMove={(e) => mouseX.set(e.pageX)}
            onMouseLeave={() => mouseX.set(Infinity)}
        >
            {content}
        </motion.div>
    )
}
